import { Button } from '@heidi/design-system/button';
import { useIsTruncated } from '@heidi/design-system/hooks/use-is-truncated';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { cn } from '@heidi/utils';
import { isToday, startOfDay } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useIntl, type IntlShape, type MessageDescriptor } from 'react-intl';

import {
  linkedContextItemKey,
  type LinkedContextItem,
} from '../../../contracts/projects/linked-context';
import { resolvePatientLabel } from '../../../data/projects/patientListName';
import { sessionTitleNamesPatient } from '../../../data/projects/session-token-label';
import { TokenMark } from '../../composer/ChatTokenChip';
import { objectReferenceMetadataMessages } from '../../composer/object-reference-metadata.messages';

import {
  libraryDocumentKindMessages,
  contextMentionListMessages as messages,
} from './context-mention-list.messages';
import type { ContextMentionListProps } from './ContextMentionList';

export type ObjectContextMentionListProps = Omit<ContextMentionListProps, 'query'> & {
  readonly templateItems?: readonly LinkedContextItem[];
  readonly taskItems?: readonly LinkedContextItem[];
  readonly collectionItems?: readonly LinkedContextItem[];
};

const SECTIONS = [
  { type: 'patient', heading: messages.patients },
  { type: 'session', heading: messages.sessions },
  { type: 'task', heading: messages.tasks },
  { type: 'library_document', heading: messages.documents },
  { type: 'collection', heading: messages.collections },
  { type: 'template', heading: messages.templates },
] as const satisfies ReadonlyArray<{
  type: LinkedContextItem['type'];
  heading: MessageDescriptor;
}>;

/** Items in the order the picker numbers its rows; the editor's `@` items must match it. */
export const orderObjectMentionItems = (
  items: readonly LinkedContextItem[]
): LinkedContextItem[] =>
  SECTIONS.flatMap(({ type }) => items.filter((item) => item.type === type));

export const ObjectContextMentionList = ({
  sessionItems,
  patientItems,
  libraryDocumentItems,
  templateItems = [],
  taskItems = [],
  collectionItems = [],
  activeIndex,
  listboxId,
  optionId,
  onHover,
  onSelect,
  anchored = false,
  listMaxHeight,
}: ObjectContextMentionListProps): JSX.Element => {
  const intl = useIntl();
  const ordered = orderObjectMentionItems([
    ...sessionItems,
    ...patientItems,
    ...taskItems,
    ...libraryDocumentItems,
    ...collectionItems,
    ...templateItems,
  ]);
  const sections = SECTIONS.map(({ type, heading }) => ({
    heading,
    rows: ordered.flatMap((item, index) => (item.type === type ? [{ item, index }] : [])),
  }));

  return (
    <TooltipProvider delayDuration={300}>
      <div
        id={listboxId}
        role="listbox"
        aria-label={intl.formatMessage(messages.objectsLabel)}
        data-testid="context-mention-autocomplete"
        className={cn(
          anchored
            ? 'w-full'
            : 'absolute bottom-full start-0 z-50 mb-2 w-[378px] max-w-[calc(100vw-3rem)]',
          'flex flex-col overflow-hidden rounded-xl bg-surface shadow-lg'
        )}
      >
        {ordered.length === 0 ? (
          <div className="p-3 text-xs text-text-tertiary">
            {intl.formatMessage(messages.noMatches)}
          </div>
        ) : (
          <div
            className={cn(
              listMaxHeight == null && 'max-h-80',
              'min-h-0 flex-1 overflow-y-auto p-2'
            )}
            style={listMaxHeight == null ? undefined : { maxHeight: listMaxHeight }}
          >
            {sections.map(({ heading, rows }) =>
              rows.length === 0 ? null : (
                <section key={heading.id} className="flex flex-col">
                  <span className="px-2 pb-1 pt-2 text-xs font-normal text-text-secondary">
                    {intl.formatMessage(heading)}
                  </span>
                  {rows.map(({ item, index }) => (
                    <ObjectMentionRow
                      key={linkedContextItemKey(item)}
                      id={optionId(index)}
                      item={item}
                      index={index}
                      isActive={activeIndex === index}
                      onHover={onHover}
                      onSelect={onSelect}
                    />
                  ))}
                </section>
              )
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

interface ObjectMentionRowProps {
  id: string;
  item: LinkedContextItem;
  index: number;
  isActive: boolean;
  onHover: (index: number) => void;
  onSelect: (item: LinkedContextItem) => void;
}

const ObjectMentionRow = ({
  id,
  item,
  index,
  isActive,
  onHover,
  onSelect,
}: ObjectMentionRowProps): JSX.Element => {
  const intl = useIntl();
  const rowRef = useRef<HTMLButtonElement>(null);
  const { title, when } = objectMentionLabel(item);
  const meta = objectMentionMeta(item, intl);
  const { ref: titleRef, isTruncated } = useIsTruncated<HTMLSpanElement>(title);

  useEffect(() => {
    if (isActive) rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isActive]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          ref={rowRef}
          type="button"
          variant="ghost"
          size="md"
          disableAutomaticTypographyClasses
          id={id}
          role="option"
          aria-selected={isActive}
          onMouseEnter={() => onHover(index)}
          onMouseDown={(event) => {
            event.preventDefault();
            onSelect(item);
          }}
          className={cn(
            'h-9 w-full min-w-0 justify-start gap-2 rounded-md p-2 text-start text-text-secondary hover:bg-surface-hover hover:text-text-secondary active:bg-surface-hover [&_svg]:size-4.5',
            isActive && 'bg-surface-hover'
          )}
        >
          {item.type === 'patient' || item.type === 'session' ? (
            <TokenMark
              kind={item.type}
              label={title}
              patientName={itemPatientLabel(item)}
              avatarSeed={item.avatarSeed}
            />
          ) : null}
          <span
            ref={titleRef}
            className={cn(
              'min-w-0 flex-1 truncate text-sm font-normal',
              item.type === 'task' && item.status === 'in_progress'
                ? 'text-text-secondary'
                : 'text-text-primary'
            )}
          >
            {title}
            {when ? <span className="text-text-secondary"> · {when}</span> : null}
          </span>
          {meta ? (
            <span className="max-w-[48%] shrink-0 truncate text-xs font-normal text-text-secondary">
              {meta}
            </span>
          ) : null}
        </Button>
      </TooltipTrigger>
      {isTruncated ? (
        <TooltipContent side="top" align="start">
          {when ? `${title} · ${when}` : title}
        </TooltipContent>
      ) : null}
    </Tooltip>
  );
};

// The same label the token and the sent chip show, so a pick reads the same everywhere.
const itemPatientLabel = (item: LinkedContextItem): string | undefined =>
  item.type === 'patient' || item.type === 'session' || item.type === 'task'
    ? resolvePatientLabel(null, item.patientName)
    : undefined;

// A session row is its title, then its date-time in the quieter colour, ellipsized as one run.
const objectMentionLabel = (
  item: LinkedContextItem
): { title: string; when?: string } => {
  switch (item.type) {
    case 'patient':
      return { title: itemPatientLabel(item) ?? '' };
    case 'session':
      return item.sessionTitle && item.dateLabel
        ? { title: item.sessionTitle, when: item.dateLabel }
        : { title: item.dateLabel ?? item.tokenLabel ?? item.sessionName };
    case 'library_document':
      return { title: item.filename };
    case 'template':
    case 'task':
    case 'collection':
      return { title: item.label };
  }
};

const joinMeta = (parts: ReadonlyArray<string | undefined>): string | undefined =>
  parts.filter(Boolean).join(' · ') || undefined;

// `YYYY-MM-DD`, read as a local date so "today" doesn't slip a day across time zones.
const taskDueLabel = (
  dueDate: string | undefined,
  intl: IntlShape
): string | undefined => {
  const [year, month, day] = dueDate?.slice(0, 10).split('-').map(Number) ?? [];
  if (!year || !month || !day) return undefined;
  const date = new Date(year, month - 1, day);
  if (date < startOfDay(new Date())) return intl.formatMessage(messages.taskOverdue);
  if (isToday(date)) return intl.formatMessage(messages.taskDueToday);
  return intl.formatDate(date, { day: 'numeric', month: 'short' });
};

const objectMentionMeta = (
  item: LinkedContextItem,
  intl: IntlShape
): string | undefined => {
  switch (item.type) {
    case 'patient':
      return joinMeta([
        item.sexAge,
        item.lastSeen
          ? intl.formatMessage(objectReferenceMetadataMessages.lastSeen, {
              date: item.lastSeen,
            })
          : undefined,
      ]);
    case 'session': {
      const patientName = itemPatientLabel(item);
      return joinMeta([
        sessionTitleNamesPatient(item.sessionTitle, patientName)
          ? undefined
          : patientName,
        item.duration,
      ]);
    }
    case 'library_document':
      return intl.formatMessage(libraryDocumentKindMessages[item.documentKind]);
    case 'template':
      return item.formTemplate ? intl.formatMessage(messages.formTemplate) : undefined;
    case 'task':
      return joinMeta([itemPatientLabel(item), taskDueLabel(item.dueDate, intl)]);
    case 'collection':
      return item.documentCount === undefined
        ? undefined
        : intl.formatMessage(messages.collectionDocumentCount, {
            count: item.documentCount,
          });
  }
};
